'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var baseBus = require('./base-bus.js');
var payloadMismatchError = require('./payload-mismatch-error.js');

var logCss = 'background-color: darkgreen; color: white';
// after this timeout, pending queries are rejected
exports.PendingQueryTimeoutInMs = 10000;
var QueryBus = /** @class */ (function (_super) {
    tslib.__extends(QueryBus, _super);
    function QueryBus() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._pendingQueries = new Map();
        _this._pendingTimeouts = new Map();
        return _this;
    }
    /**
     * Subscribe to an event channel triggering callback on received event matching type.
     * @param channel - name of event channel to receive data from
     * @param callback - function executed on when event channel receives new data
     * @returns object containing an unsubscribe method
     */
    QueryBus.prototype.subscribe = function (channel, callback) {
        if (typeof callback !== 'function') {
            throw new Error('Callback function must be supplied as the second argument.');
        }
        if (!!this.getHandler(channel)) {
            throw new Error("Handler for this query already registered (channel = " + channel + ").");
        }
        this.logRegisteredHandler(channel);
        this.setHandler(channel, callback);
        if (this._pendingQueries.has(channel)) {
            this.logPendingQueries(channel);
            this.resolvePendingQueries(channel);
        }
        return this.getSubscription(channel);
    };
    QueryBus.prototype.query = function (channel, param2, param3) {
        var payload = typeof param2 !== 'function' ? param2 : undefined;
        var callback = typeof param3 !== 'undefined'
            ? param3
            : typeof param2 === 'function'
                ? param2
                : undefined;
        if (callback && typeof callback !== 'function') {
            throw new Error('Callback provided is not a function.');
        }
        if (!this.getHandler(channel)) {
            this.logPendingQuery(channel, payload);
            return this.savePendingQuery(channel, payload, callback);
        }
        var schema = this.getSchema(channel);
        if (this.isPayloadInvalid(channel, payload)) {
            throw new payloadMismatchError.PayloadMismatchError(channel, schema, payload);
        }
        this.logQuery(channel, payload);
        this.setLatest(channel, payload);
        var handlerResult = this.resolveHandler(channel, payload);
        return callback ? handlerResult.then(callback) : handlerResult;
    };
    /**
     * Overrides the PendingQueryTimeoutInMs default value
     * @param timeoutInMs -  desired threshold, in milliseconds
     */
    QueryBus.prototype.overridePendingQueryTimeoutInMs = function (timeoutInMs) {
        exports.PendingQueryTimeoutInMs = timeoutInMs;
    };
    QueryBus.prototype.getHandler = function (channel) {
        var _a;
        return (_a = this._subscriptions.get(channel)) === null || _a === void 0 ? void 0 : _a.handler;
    };
    QueryBus.prototype.setHandler = function (channel, callback) {
        if (!this._subscriptions.has(channel)) {
            this.initChannel(channel);
        }
        this._subscriptions.get(channel).handler = callback;
    };
    QueryBus.prototype.resolveHandler = function (channel, payload) {
        var _this = this;
        return new Promise(function (resolve, reject) { return tslib.__awaiter(_this, void 0, void 0, function () {
            var result, e_1;
            return tslib.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.getHandler(channel)({
                                channel: channel,
                                payload: payload,
                            })];
                    case 1:
                        result = _a.sent();
                        resolve(result);
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _a.sent();
                        reject(e_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); });
    };
    QueryBus.prototype.getSubscription = function (channel) {
        var _this = this;
        return {
            unsubscribe: function () { return delete _this._subscriptions.get(channel).handler; },
        };
    };
    QueryBus.prototype.savePendingQuery = function (channel, payload, callback) {
        if (!this._pendingQueries.has(channel)) {
            this._pendingQueries.set(channel, []);
            this.initPendingQueryTimer(channel);
        }
        if (callback) {
            this._pendingQueries
                .get(channel)
                .push({ payload: payload, resolve: callback });
        }
        else {
            var entry_1 = { payload: payload };
            var promise = new Promise(function (resolve, reject) {
                entry_1.resolve = resolve;
                entry_1.reject = reject;
            });
            this._pendingQueries.get(channel).push(entry_1);
            return promise;
        }
    };
    QueryBus.prototype.initPendingQueryTimer = function (channel) {
        var _this = this;
        this._pendingTimeouts.set(channel, setTimeout(function () {
            var entries = _this._pendingQueries.get(channel);
            entries.forEach(function (entry) {
                var _a;
                (_a = entry.reject) === null || _a === void 0 ? void 0 : _a.call(entry, new Error("Pending query for more than " + exports.PendingQueryTimeoutInMs / 1000 + " seconds, channel = " + channel + ", payload = " + JSON.stringify(entry.payload)));
            });
            _this._pendingQueries.delete(channel);
        }, exports.PendingQueryTimeoutInMs));
    };
    QueryBus.prototype.resolvePendingQueries = function (channel) {
        var _this = this;
        clearTimeout(this._pendingTimeouts.get(channel));
        this._pendingTimeouts.delete(channel);
        this._pendingQueries.get(channel).forEach(function (entry) { return tslib.__awaiter(_this, void 0, void 0, function () {
            var result, e_2;
            var _a;
            return tslib.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.resolveHandler(channel, entry.payload)];
                    case 1:
                        result = _b.sent();
                        entry.resolve(result);
                        return [3 /*break*/, 3];
                    case 2:
                        e_2 = _b.sent();
                        (_a = entry.reject) === null || _a === void 0 ? void 0 : _a.call(entry, e_2);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); });
        this._pendingQueries.delete(channel);
    };
    QueryBus.prototype.logRegisteredHandler = function (channel) {
        this.log('%cQuery bus: registered handler for channel', logCss, channel);
    };
    QueryBus.prototype.logPendingQuery = function (channel, payload) {
        this.log('%cQuery bus: handler is missing for channel', logCss, channel);
        this.log('%cQuery bus: saving query with payload', logCss, payload);
    };
    QueryBus.prototype.logQuery = function (channel, payload) {
        this.log('%cQuery bus: resolving query for channel', logCss, channel);
        this.log('%cQuery bus: query payload', logCss, payload);
    };
    QueryBus.prototype.logPendingQueries = function (channel) {
        this.log('%cQuery bus: resolving pending queries for channel', logCss, channel);
        this.log('%cQuery bus: pending payloads', logCss, Array.from(this._pendingQueries.values()));
    };
    return QueryBus;
}(baseBus.BaseBus));

exports.QueryBus = QueryBus;
